import styled from 'styled-components';
import {dFlex, dFlexCenter, dFlexColumn, alignCenter, justifyEnd, justifyCenter, justifyBetween, alignStart, justifyStart, dFlexStart} from '../../../globalVariables/GlobalFlex';
import {getFontSize, getFontWeight, getToken, getSpacing, getPrimitive} from '@tickchak/react-share/app';
import {mobileMedia, scrollbar} from '../../../globalVariables/GlobalVariables';
import { loaderContainer } from '../../../component/loader/Loader.css';
import {header} from '../../../component/newTable/header/Header.css';
import {row} from '../../../component/newTable/newRow/Row.css';
import {tableContainer} from '../../../component/newTable/Table.css';

// Colors
const GREEN_BG = getPrimitive('greens.green50');
const GREEN_TEXT = getPrimitive('greens.green600');
const RED_BG = getPrimitive('reds.red50');
const RED_TEXT = getPrimitive('reds.red400');

// Header
export const popupHeader = styled.div`
    ${dFlexStart}
    padding: ${getSpacing('spaceXl')};
    gap: ${getSpacing('spaceSm')};
    border-bottom: 0.5px solid ${getToken('color.border.subtle')};
    background: ${getToken('color.bg.base')};
    flex-shrink: 0;
`;

export const headerTitle = styled.span`
    font-size: ${getFontSize('body')};
    font-weight: ${getFontWeight('bold')};
    color: ${getToken('color.text.default')};
`;

// Content area
export const content = styled.div`
    ${dFlexColumn}
    ${alignStart}
    padding: 0 ${getSpacing('spaceXl')} ${getSpacing('spaceXl')};
    gap: ${getSpacing('spaceLg')};
    overflow-y: auto;
    flex: 1;
    ${scrollbar}

    ${loaderContainer} {
        width: 100%;
    }

    ${mobileMedia} {
        padding: ${getSpacing('spaceLg')};
    }

    ${header} {
        border-top: none;
    }

    ${row} {
        box-shadow: none;
        border-bottom: 1px solid ${getToken('color.border.disabled')};
    }
        ${tableContainer}{
            height: 100%;
        }
`;

// Table header - Desktop only
export const tableHeader = styled.div`
    ${dFlex}
    ${alignCenter}
    ${justifyBetween}
    width: 100%;
    padding: ${getSpacing('spaceMd')} 0;
    gap: ${getSpacing('spaceMd')};

    ${mobileMedia} {
        display: none;
    }
`;

export const headerCell = styled.div<{$width?: string; $minWidth?: string}>`
    ${dFlex}
    ${alignCenter}
    ${justifyCenter}
    gap: ${getSpacing('spaceSm')};
    font-size: ${getFontSize('caption')};
    font-weight: ${getFontWeight('bold')};
    color: ${getToken('color.text.default')};
    text-align: center;
    width: ${p => p.$width || 'auto'};
    min-width: ${p => p.$minWidth || 'auto'};
    flex-shrink: 0;

    svg {
        width: 16px;
        height: 16px;
    }
`;

export const nameHeaderCell = styled(headerCell)`
    width: 250px;
    min-width: 200px;
    ${justifyStart}
`;

// Table body
export const tableBody = styled.div`
    ${dFlexColumn}
    gap: ${getSpacing('spaceSm')};
    width: 100%;

    ${mobileMedia} {
        gap: ${getSpacing('spaceLg')};
    }
`;

// Table row - transforms to card on mobile
// Note: This is wrapped by newTable's Row component, so we don't need box-shadow/border-radius here
export const tableRow = styled.div`
    ${dFlex}
    ${alignCenter}
    ${justifyBetween}
    width: 100%;
    padding: ${getSpacing('spaceSm')} 0;
    gap: ${getSpacing('spaceMd')};
    border-bottom: 1px solid ${getToken('color.border.subtle')};

    &:last-child {
        border-bottom: none;
    }

    ${mobileMedia} {
        ${dFlexColumn}
        ${alignStart}
        border: 1px solid ${getToken('color.border.disabled')};
        border-radius: 10px;
        padding: 0;
        gap: 0;
        overflow: hidden;
    }
`;

// Mobile-only labels
export const mobileLabel = styled.span`
    display: none;
    font-size: ${getFontSize('bodySmall')};
    font-weight: ${getFontWeight('regular')};
    color: ${getToken('color.text.default')};
    text-align: start;
    width: 100%;

    ${mobileMedia} {
        display: block;
    }
`;

export const mobileScoresLabel = styled.span`
    display: none;

    ${mobileMedia} {
        display: block;
        font-size: ${getFontSize('bodySmall')};
        font-weight: ${getFontWeight('regular')};
        color: ${getToken('color.text.default')};
        text-align: start;
        width: 100%;
        padding: 0 ${getSpacing('spaceLg')};
        order: 2;
    }
`;

export const mobileSectionLabel = styled.span`
    display: none;

    ${mobileMedia} {
        display: block;
        font-size: ${getFontSize('bodySmall')};
        font-weight: ${getFontWeight('bold')};
        color: ${getToken('color.text.default')};
        text-align: start;
        width: 100%;
        padding: ${getSpacing('spaceLg')};
        border-top: 1px solid ${getToken('color.border.subtle')};

        &:nth-of-type(2) {
            order: 5;
        }

        &:nth-of-type(3) {
            order: 8;
        }
    }
`;

// Video name cell
export const videoNameCell = styled.div`
    ${dFlex}
    ${alignCenter}
    ${justifyStart}
    width: 250px;
    min-width: 200px;
    max-width: 300px;
    flex-shrink: 0;

    ${mobileMedia} {
        ${dFlexColumn}
        ${alignStart}
        width: 100%;
        max-width: 100%;
        background: ${getToken('color.bg.surface.default')};
        padding: ${getSpacing('spaceSm')} ${getSpacing('spaceLg')};
        gap: ${getSpacing('spaceSm')};
        border-bottom: 1px solid ${getToken('color.border.subtle')};
        order: 1;
    }
`;

export const videoName = styled.span`
    font-size: ${getFontSize('body')};
    font-weight: ${getFontWeight('regular')};
    color: ${getToken('color.text.default')};
    text-align: start;
    opacity: 0.5;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    ${mobileMedia} {
        font-weight: ${getFontWeight('bold')};
        opacity: 1;
        text-align: start;
        width: 100%;
        white-space: normal;
    }
`;

// Scores wrapper - keeps scores side by side on mobile
export const scoresWrapper = styled.div`
    display: contents;

    ${mobileMedia} {
        ${dFlex}
        ${alignCenter}
        width: 100%;
        order: 3;
    }
`;

// Score cell
export const scoreCell = styled.div`
    ${dFlex}
    ${alignCenter}
    ${justifyCenter}
    width: 98px;
    flex-shrink: 0;

    ${mobileMedia} {
        ${dFlexColumn}
        ${alignCenter}
        flex: 1;
        width: auto;
        gap: 10px;
        padding: ${getSpacing('spaceLg')};
    }
`;

export const scoreCellLabel = styled.div`
    display: none;

    ${mobileMedia} {
        ${dFlex}
        ${alignCenter}
        ${justifyCenter}
        gap: ${getSpacing('spaceSm')};
        width: 100%;
        font-size: ${getFontSize('bodySmall')};
        font-weight: ${getFontWeight('bold')};
        color: ${getToken('color.text.default')};

        svg {
            width: 16px;
            height: 16px;
        }
    }
`;

// Score badge
export const scoreBadge = styled.div<{$variant: 'green' | 'red'}>`
    ${dFlexCenter}
    gap: ${getSpacing('spaceSm')};
    padding: 0 ${getSpacing('spaceLg')};
    height: 44px;
    border-radius: 100px;
    background: ${p => p.$variant === 'green' ? GREEN_BG : RED_BG};
    white-space: nowrap;

    ${mobileMedia} {
        height: 32px;
        width: 100%;
    }
`;

export const scoreNumber = styled.span<{$variant: 'green' | 'red'}>`
    font-size: ${getFontSize('body')};
    font-weight: ${getFontWeight('bold')};
    color: ${p => p.$variant === 'green' ? GREEN_TEXT : RED_TEXT};
`;

export const scorePercent = styled.span<{$variant: 'green' | 'red'}>`
    font-size: ${getFontSize('body')};
    font-weight: ${getFontWeight('regular')};
    color: ${p => p.$variant === 'green' ? GREEN_TEXT : RED_TEXT};
`;

// Chart and comments wrapper - keeps them side by side on mobile
export const chartCommentsWrapper = styled.div`
    display: contents;

    ${mobileMedia} {
        ${dFlex}
        ${alignCenter}
        width: 100%;
        padding: 0 ${getSpacing('spaceLg')} ${getSpacing('spaceLg')};
        gap: ${getSpacing('spaceMd')};
        order: 6;
    }
`;

// Chart cell
export const chartCell = styled.div`
    ${dFlexCenter}
    width: 100px;
    flex-shrink: 0;

    ${mobileMedia} {
        ${dFlexColumn}
        ${alignCenter}
        flex: 1;
        background: ${getPrimitive('grays.gray100')};
        border-radius: 5px;
        padding: ${getSpacing('spaceLg')};
        height: 105px;
        gap: 10px;
    }
`;

export const chartCellLabel = styled.span`
    display: none;

    ${mobileMedia} {
        display: block;
        font-size: ${getFontSize('caption')};
        font-weight: ${getFontWeight('regular')};
        color: ${getToken('color.text.subtle')};
        text-align: center;
    }
`;

// Donut chart container
export const chartContainer = styled.div`
    ${dFlexCenter}
    position: relative;
    width: 76px;
    height: 50px;
`;

export const chartLabel = styled.div`
    ${dFlexColumn}
    ${alignCenter}
    ${justifyCenter}
    text-align: center;
`;

export const chartPercent = styled.span`
    font-size: ${getFontSize('caption')};
    font-weight: ${getFontWeight('bold')};
    color: ${getToken('color.text.default')};
    line-height: 1;
`;

// Comments count cell
export const commentsCountCell = styled.div`
    ${dFlexCenter}
    width: 60px;
    flex-shrink: 0;

    ${mobileMedia} {
        ${dFlexColumn}
        ${alignCenter}
        flex: 1;
        background: ${getPrimitive('grays.gray100')};
        border-radius: 5px;
        padding: ${getSpacing('spaceLg')};
        height: 105px;
        gap: ${getSpacing('spaceLg')};
    }
`;

export const commentsCount = styled.span`
    font-size: ${getFontSize('body')};
    font-weight: ${getFontWeight('regular')};
    color: ${getToken('color.text.default')};

    ${mobileMedia} {
        font-size: 22px;
        font-weight: ${getFontWeight('bold')};
        line-height: 1.3;
    }
`;

// Views count cell
export const viewsCountCell = styled.div`
    ${dFlexCenter}
    width: 80px;
    flex-shrink: 0;

    ${mobileMedia} {
        ${dFlexColumn}
        ${alignStart}
        width: 100%;
        padding: ${getSpacing('spaceLg')};
        gap: ${getSpacing('spaceSm')};
        order: 7;
    }
`;

export const viewsCount = styled.span`
    font-size: ${getFontSize('body')};
    font-weight: ${getFontWeight('regular')};
    color: ${getToken('color.text.default')};

    ${mobileMedia} {
        font-size: ${getFontSize('body')};
        font-weight: ${getFontWeight('bold')};
    }
`;

// Actions cell
export const actionsCell = styled.div`
    ${dFlex}
    ${alignCenter}
    gap: ${getSpacing('spaceLg')};
    width: 213px;
    min-width: 180px;

    ${mobileMedia} {
        width: 100%;
        padding: 0 ${getSpacing('spaceLg')} ${getSpacing('spaceLg')};
        gap: ${getSpacing('spaceMd')};
        order: 9;

        button {
            flex: 1;
        }
    }
`;

// Empty state
export const emptyState = styled.div`
    ${dFlexCenter}
    width: 100%;
    padding: ${getSpacing('space2xl')};
    font-size: ${getFontSize('body')};
    color: ${getToken('color.text.subtle')};
`;

// Rotated icon for thumbs down
export const rotatedIcon = styled.span`
    ${dFlexCenter}
    transform: scaleY(-1);
`;
